/**
 * Tasks Data Hook
 *
 * Provides task data for both Demo and Live modes.
 * In Live mode, fetches from the Tasks API.
 * In Demo mode, returns mock data from the existing useTaskBoard hook.
 */

import { useQueryClient } from "@tanstack/react-query";
import { useModeAwareQuery, useModeAwareMutation } from "./useModeAwareQuery";
import { useApi } from "./useApi";
import type {
  GetTasksApiResponse,
  TaskListItemApi,
  TaskTypeLookupApi,
  TasksApiQueryParams,
  CreateTaskApiRequest,
  UpdateTaskApiRequest,
} from "@/types/api/tasks";
import type { UnifiedTask, TaskPriority, TaskStatus } from "@/types/tasks";

/**
 * Build the deep-link URL for the source record linked to a task.
 * Falls back to highlighting the task on the tasks page when there is no link.
 */
function buildSourceUrl(item: TaskListItemApi): string {
  if (item.linkId == null) {
    return `/tasks?highlight=${item.taskId}`;
  }

  const typeName = (item.taskTypeName ?? "").toLowerCase();

  if (typeName.includes("promise")) {
    return `/collections/promises/${item.linkId}`;
  }
  if (typeName.includes("collection") || typeName.includes("case")) {
    return `/collections/queue/${item.linkId}`;
  }
  if (typeName.includes("dispute")) {
    return `/disputes/${item.linkId}`;
  }

  return `/tasks?highlight=${item.taskId}`;
}

/**
 * Map a single API task item to the frontend UnifiedTask model
 */
function mapApiTaskToUnified(item: TaskListItemApi): UnifiedTask {
  return {
    id: String(item.taskId),
    type: (item.taskType?.toLowerCase() ?? "task") as UnifiedTask["type"],
    title: item.title,
    description: item.description ?? "",
    priority: (item.priority ?? "Medium") as TaskPriority,
    status: (item.status?.toLowerCase() === "completed" ? "completed" : "open") as TaskStatus,
    dueDate: new Date(item.dueDate),
    assignedTo: item.assignedUserName ?? "Unassigned",
    assignedToId: String(item.userAssignedId),
    customerId: item.customerId != null ? String(item.customerId) : "",
    customerName: item.customerName ?? "",
    amount: undefined,
    sourceType: item.taskTypeName ?? "Task",
    sourceId: item.linkId != null ? String(item.linkId) : String(item.taskId),
    sourceUrl: buildSourceUrl(item),
    createdAt: new Date(item.createdAt),
    updatedAt: new Date(item.updatedAt),
    tags: [item.taskTypeName ?? "Task", item.statusName ?? ""],
  };
}

/**
 * Hook to fetch tasks list from the API
 * In demo mode returns empty array (the page will use useTaskBoard instead)
 */
export function useTasksList(params?: TasksApiQueryParams) {
  const { apiGet } = useApi();

  return useModeAwareQuery<UnifiedTask[], Error>(
    ["tasks-list", params],
    async ({ signal }) => {
      const response = await apiGet<GetTasksApiResponse>(
        "/api/v1/tasks",
        params,
        signal
      );
      return response.data.map(mapApiTaskToUnified);
    },
    [], // Demo mode returns empty - the page uses useTaskBoard for demo
    {
      staleTime: 0,
      refetchOnMount: "always",
    }
  );
}

const mockTaskTypes: TaskTypeLookupApi[] = [
  { code: "collection_case", name: "Collections" },
  { code: "promise", name: "Promises" },
  { code: "dispute", name: "Disputes" },
  { code: "transaction", name: "Receipts" },
  { code: "task", name: "Task" },
];

/**
 * Hook to fetch task type lookup options from the API
 */
export function useTaskTypeLookups() {
  const { apiGet } = useApi();

  return useModeAwareQuery<TaskTypeLookupApi[], Error>(
    ["task-type-lookups"],
    async ({ signal }) => {
      return apiGet<TaskTypeLookupApi[]>("/api/v1/tasks/types", undefined, signal);
    },
    mockTaskTypes,
    { staleTime: 60 * 1000, refetchOnWindowFocus: false }
  );
}

/**
 * Hook to create a new task via the API.
 * In demo mode returns a mock response. On success invalidates the tasks list.
 */
export function useCreateTaskMutation() {
  const { apiPost } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<TaskListItemApi, Error, CreateTaskApiRequest>(
    async (data) => {
      return apiPost<TaskListItemApi>("/api/v1/tasks", data);
    },
    (data) => {
      const now = new Date().toISOString();
      return {
        taskId: Date.now(),
        title: data.title,
        description: data.description ?? null,
        taskType: "Task",
        taskTypeName: "Task",
        status: "Open",
        statusName: "Open",
        dueDate: data.dueDate,
        priority: null,
        userAssignedId: data.userAssignedId,
        assignedUserName: "Demo User",
        customerId: data.customerId ?? null,
        customerName: null,
        customerAccountNumber: null,
        linkId: null,
        createdByUserId: 0,
        createdByName: "Demo User",
        createdAt: now,
        updatedAt: now,
        completedAt: null,
        completedByUserId: null,
        completedByName: null,
        daysOverdue: null,
      };
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["tasks-list"] });
      },
    }
  );
}

/**
 * Hook to update an existing task via the API.
 * In demo mode returns a mock response. On success invalidates the tasks list.
 */
export function useUpdateTaskMutation() {
  const { apiPut } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<TaskListItemApi, Error, { taskId: number; data: UpdateTaskApiRequest }>(
    async ({ taskId, data }) => {
      return apiPut<TaskListItemApi>(`/api/v1/tasks/${taskId}`, data);
    },
    ({ taskId, data }) => {
      const now = new Date().toISOString();
      return {
        taskId,
        title: data.title,
        description: data.description ?? null,
        taskType: "Task",
        taskTypeName: "Task",
        status: "Open",
        statusName: "Open",
        dueDate: data.dueDate,
        priority: null,
        userAssignedId: data.userAssignedId,
        assignedUserName: "Demo User",
        customerId: data.customerId ?? null,
        customerName: null,
        customerAccountNumber: null,
        linkId: null,
        createdByUserId: 0,
        createdByName: "Demo User",
        createdAt: now,
        updatedAt: now,
        completedAt: null,
        completedByUserId: null,
        completedByName: null,
        daysOverdue: null,
      };
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["tasks-list"] });
      },
    }
  );
}

/**
 * Hook to complete a task via the API.
 * In demo mode returns a mock response. On success invalidates the tasks list.
 */
export function useCompleteTaskMutation() {
  const { apiPost } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<TaskListItemApi, Error, number>(
    async (taskId) => {
      return apiPost<TaskListItemApi>(`/api/v1/tasks/${taskId}/complete`);
    },
    (taskId) => {
      const now = new Date().toISOString();
      return {
        taskId,
        title: "Demo Task",
        description: null,
        taskType: "Task",
        taskTypeName: "Task",
        status: "Completed",
        statusName: "Completed",
        dueDate: now,
        priority: null,
        userAssignedId: 0,
        assignedUserName: "Demo User",
        customerId: null,
        customerName: null,
        customerAccountNumber: null,
        linkId: null,
        createdByUserId: 0,
        createdByName: "Demo User",
        createdAt: now,
        updatedAt: now,
        completedAt: now,
        completedByUserId: 0,
        completedByName: "Demo User",
        daysOverdue: null,
      };
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["tasks-list"] });
      },
    }
  );
}
